import { Injectable } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  UrlTree,
} from '@angular/router'
import { LaunchService } from '../services/internal/launch.service'
import { StripeSetupService } from '../services/internal/stripe-setup.service'
import { bypassesLaunch, isStripeSetupReturn } from './launch-bypass'

/**
 * `canActivateChild` et non `canActivate` : le garde est posé sur la route
 * parente, dont le `data` ne contient pas celui de l'enfant activé. C'est le
 * snapshot de l'enfant qui porte le marqueur de contournement. En contrepartie
 * il s'évalue à chaque navigation entre pages du groupe, et non à la seule
 * entrée dans celui-ci : la condition de renvoi doit donc être un fait stable
 * du processus, pas l'URL d'où l'on vient.
 */
@Injectable({
  providedIn: 'root',
})
export class LaunchGuard implements CanActivateChild {
  constructor(
    private router: Router,
    private launchService: LaunchService,
    private stripeSetup: StripeSetupService
  ) {}

  canActivateChild(child: ActivatedRouteSnapshot): boolean | UrlTree {
    const url = new URL(window.location.href)
    const pendingStripeSetup = this.stripeSetup.peek() !== null

    if (bypassesLaunch(child, url, pendingStripeSetup)) {
      // Cette entrée n'est pas passée par l'écran de lancement et ne doit rien
      // déclencher : ni chargement des données essentielles - il courrait
      // contre l'établissement de la session native, 403 sur les routes
      // protégées - ni recherche de mise à jour. Marquer la séquence de
      // démarrage comme jouée garantit que la première visite ultérieure de
      // `/launch`, à la sortie du parcours, ne fera que router et charger :
      // pas de demande de suivi, pas de `checkForUpdate`, donc aucun
      // redémarrage de l'application au milieu du parcours.
      this.launchService.markLaunchSequenceRun()

      // Un retour Stripe atterrit sur une page qui a besoin du programme et
      // des forfaits, et qui ne les recevra pas de l'écran de lancement
      // puisqu'elle vient de le contourner : on les charge en arrière-plan,
      // sans bloquer l'affichage ni rediriger. Les autres pages contournantes
      // (mentions, CGU, location) n'en dépendent pas et ne déclenchent rien.
      if (isStripeSetupReturn(url) || pendingStripeSetup) {
        void this.launchService.loadEssentialDataInBackground()
      }

      return true
    }

    // La séquence de démarrage a-t-elle été jouée dans ce processus ? C'est ce
    // que la version d'origine approximait en regardant si la navigation
    // précédente venait de `/launch` - approximation qui ne tient pas ici :
    // le garde n'est instancié qu'à la première activation d'un enfant, donc
    // après le passage par `/launch`, qu'il ne voit jamais. Il renvoyait alors
    // vers l'écran de lancement une application qui en sortait tout juste, et
    // la seconde traversée, en mode « déjà démarré », attendait un chargement
    // sans borne : logo figé.
    if (this.launchService.hasRunLaunchSequence()) {
      return true
    }

    const queryParams: Record<string, string> = {
      next: window.location.pathname,
    }
    url.searchParams.forEach((value, key) => {
      queryParams[key] = value
    })
    return this.router.createUrlTree(['/launch'], { queryParams })
  }
}
